import React from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  IconButton,
  Divider,
  useToast,
  Badge,
  Heading,
  useColorModeValue,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { FiTrash2, FiArrowLeft, FiShoppingCart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import type { Product } from "../types";

const Cart: React.FC = () => {
  const { items: cart, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const total = cart.reduce<number>((sum, item) => {
    return sum + item.price * item.quantity;
  }, 0);

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      toast({
        title: "Item removed",
        description: "The item has been removed from your cart",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId: number) => {
    removeFromCart(productId);
    toast({
      title: "Item removed",
      description: "The item has been removed from your cart",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClearCart = () => {
    clearCart();
    toast({
      title: "Cart cleared",
      description: "All items have been removed from your cart",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast({
        title: "Cart is empty",
        description:
          "Please add items to your cart before proceeding to checkout.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    navigate("/checkout");
  };

  return (
    <Container maxW="1400px" py={8}>
      <VStack spacing={8} align="stretch">
        <HStack>
          <IconButton
            aria-label="Go back"
            icon={<FiArrowLeft />}
            onClick={() => navigate(-1)}
            variant="ghost"
          />
          <Heading size="lg">Shopping Cart</Heading>
          <Badge colorScheme="green" fontSize="md" px={3} py={1}>
            {cart.length} {cart.length === 1 ? "item" : "items"}
          </Badge>
        </HStack>

        {cart.length === 0 ? (
          <VStack spacing={4} py={12}>
            <Text fontSize="xl" color="gray.500">
              Your cart is empty
            </Text>
            <Button colorScheme="green" onClick={() => navigate("/")}>
              Continue Shopping
            </Button>
          </VStack>
        ) : (
          <>
            <VStack spacing={4} align="stretch">
              {cart.map((item) => (
                <Box
                  key={item.id}
                  bg={bgColor}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <HStack spacing={4} align="center">
                    <VStack spacing={2} width="100px">
                      <Image
                        src={item.image}
                        alt={item.name}
                        objectFit="cover"
                        borderRadius="md"
                        width="100px"
                        height="100px"
                      />
                      <IconButton
                        aria-label="Remove item"
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleRemoveItem(item.id)}
                        width="100%"
                      />
                    </VStack>
                    <VStack flex={1} align="start" spacing={1}>
                      <Text fontWeight="bold" fontSize="lg">
                        {item.name}
                      </Text>
                      <Text color="gray.600" noOfLines={2}>
                        {item.description}
                      </Text>
                      <HStack spacing={4}>
                        <Text fontSize="lg" fontWeight="bold" color="green.500">
                          Rs. {item.price.toFixed(2)}
                        </Text>
                        <NumberInput
                          size="sm"
                          min={1}
                          max={item.stock}
                          value={item.quantity}
                          onChange={(value) =>
                            handleQuantityChange(item.id, parseInt(value))
                          }
                          width="100px"
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>

            <Divider />

            <Box
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={6}
            >
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="lg">Subtotal</Text>
                  <Text fontSize="lg">Rs. {total.toFixed(2)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="lg">Shipping</Text>
                  <Text fontSize="lg" color="green.500">
                    Free
                  </Text>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="xl" fontWeight="bold">
                    Total
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.500">
                    Rs. {total.toFixed(2)}
                  </Text>
                </HStack>
                <HStack spacing={4}>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    size="lg"
                    onClick={handleClearCart}
                    flex={1}
                  >
                    Clear Cart
                  </Button>
                  <Button
                    colorScheme="green"
                    size="lg"
                    onClick={handleCheckout}
                    flex={1}
                    leftIcon={<FiShoppingCart />}
                  >
                    Proceed to Checkout
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </>
        )}
      </VStack>
    </Container>
  );
};

export default Cart;
